// Shared call constants. MAX_CALL_PEERS mirrors calls.MaxPeers on the server —
// the server is the authority (it rejects an oversized call), this just stops
// the UI from letting you build one.
export const MAX_CALL_PEERS = 8;

/**
 * How a person is written on screen.
 *
 * Not everyone has set a display name, and the directory then hands us their
 * email — which is long, repeats the domain for every colleague, and looked
 * awful blown up as a call title. The local part is what people actually call
 * each other, so an email collapses to it and anything else passes through
 * untouched.
 */
export function displayName(raw: string): string {
  const name = (raw ?? '').trim();
  const at = name.indexOf('@');
  if (at <= 0 || name.includes(' ')) return name; // a real name, or nothing
  return name.slice(0, at);
}

/** "Ali, Sara and 2 others" — how a group reads in a list. */
export function peerSummary(names: string[]): string {
  const clean = names.map(displayName).filter(Boolean);
  if (clean.length === 0) return 'Group call';
  if (clean.length <= 2) return clean.join(' and ');
  return `${clean[0]}, ${clean[1]} and ${clean.length - 2} ${clean.length - 2 === 1 ? 'other' : 'others'}`;
}

/** The names of everyone on a call except you — what the call is titled with.
 * `myUserId` comes from the grant identity, so it works for caller and peer
 * alike without the page having to know which one it is. */
export function othersOn(
  call: { kind: string; callerId: string; callerName: string; calleeName: string; peers: { id: string; name: string }[] },
  myUserId: string,
): string[] {
  if (call.kind !== 'group') {
    return [myUserId === call.callerId ? call.calleeName : call.callerName].filter(Boolean);
  }
  const names = call.peers.filter((p) => p.id !== myUserId).map((p) => p.name);
  if (myUserId !== call.callerId) names.unshift(call.callerName);
  return names.filter(Boolean);
}
